import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Settings, Trash2, RefreshCw, LogOut, Bell, Check, X } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../../../utils/api';
import { ConfirmationModal } from '../../common/ConfirmationModal';
import LeaderboardList from '../../leaderboard/LeaderboardList';
import LeaderboardTable from '../../leaderboard/LeaderboardTable';
import CreateLeaderboardModal from '../../leaderboard/CreateLeaderboardModal';
import EditLeaderboardModal from '../../leaderboard/EditLeaderboardModal';

const LeaderboardPage = () => {
  const [leaderboards, setLeaderboards] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [error, setError] = useState('');

  const fetchLeaderboards = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiGet('/leaderboards');
      if (response.ok) {
        const data = await response.json();
        setLeaderboards(data.leaderboards || []);
      }
    } catch (err) {
      console.error('Error fetching leaderboards:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const response = await apiGet('/leaderboards/my_invitations');
      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations || []);
      }
    } catch (err) {
      console.error('Error fetching invitations:', err);
    }
  }, []);

  const fetchLeaderboardDetail = useCallback(async (id) => {
    try {
      setDetailLoading(true);
      const response = await apiGet(`/leaderboards/${id}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedData(data);
      }
    } catch (err) {
      console.error('Error fetching leaderboard detail:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaderboards();
    fetchInvitations();
  }, [fetchLeaderboards, fetchInvitations]);

  useEffect(() => {
    if (selectedId) {
      fetchLeaderboardDetail(selectedId);
    } else {
      setSelectedData(null);
    }
  }, [selectedId, fetchLeaderboardDetail]);

  const handleSelect = (id) => {
    setSelectedId(id);
  };

  const handleCreate = () => {
    setShowCreateModal(false);
    fetchLeaderboards();
  };

  const handleUpdate = () => {
    setShowEditModal(false);
    fetchLeaderboards();
    if (selectedId) fetchLeaderboardDetail(selectedId);
  };

  const handleDelete = () => {
    if (!selectedData?.leaderboard) return;
    setConfirmModal({
      isOpen: true,
      title: 'Delete Leaderboard',
      message: `Are you sure you want to delete "${selectedData.leaderboard.name}"? This will remove all members and rankings. This cannot be undone.`,
      confirmText: 'Delete',
      isDestructive: true,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false });
        try {
          const response = await apiDelete(`/leaderboards/${selectedData.leaderboard.id}`);
          if (response.ok) {
            setSelectedId(null);
            setSelectedData(null);
            fetchLeaderboards();
          }
        } catch (err) {
          setError(err.message);
        }
      }
    });
  };

  const handleLeave = () => {
    if (!selectedData?.leaderboard) return;
    setConfirmModal({
      isOpen: true,
      title: 'Leave Leaderboard',
      message: `Are you sure you want to leave "${selectedData.leaderboard.name}"? You will be removed from the rankings and will no longer see this leaderboard unless you are invited again.`,
      confirmText: 'Leave',
      isDestructive: true,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false });
        try {
          const response = await apiPost(`/leaderboards/${selectedData.leaderboard.id}/leave`);
          if (response.ok) {
            setSelectedId(null);
            setSelectedData(null);
            fetchLeaderboards();
          } else {
            const data = await response.json();
            setError(data.error || 'Failed to leave');
          }
        } catch (err) {
          setError(err.message);
        }
      }
    });
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      const response = await apiPost(`/leaderboards/invitations/${invitationId}/accept`);
      if (response.ok) {
        fetchInvitations();
        fetchLeaderboards();
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    try {
      const response = await apiPost(`/leaderboards/invitations/${invitationId}/decline`);
      if (response.ok) {
        fetchInvitations();
      }
    } catch (err) {
      console.error('Error declining invitation:', err);
    }
  };

  const lb = selectedData?.leaderboard;
  const rankings = selectedData?.rankings;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Trophy size={24} className="text-blue-500" />
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-300 font-unna">Leaderboards</h1>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-2 rounded mb-4 text-sm font-rasa">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-400 hover:text-red-600"><X size={14} /></button>
        </div>
      )}

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Bell size={16} className="text-blue-500" />
            <h3 className="text-sm font-medium text-blue-700 font-rasa">
              Pending Invitations ({invitations.length})
            </h3>
          </div>
          <div className="space-y-2">
            {invitations.map(inv => (
              <div key={inv.id} className="flex items-center justify-between bg-white px-4 py-2.5 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700 font-rasa">{inv.leaderboard_name}</p>
                  <p className="text-xs text-gray-400 font-rasa">
                    Invited by {inv.invited_by_full_name || inv.invited_by_username}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleAcceptInvitation(inv.id)}
                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 font-rasa flex items-center gap-1"
                  >
                    <Check size={12} /> Accept
                  </button>
                  <button
                    onClick={() => handleDeclineInvitation(inv.id)}
                    className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200 font-rasa flex items-center gap-1"
                  >
                    <X size={12} /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        <div className="md:w-64 flex-shrink-0">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <LeaderboardList
              leaderboards={leaderboards}
              selectedId={selectedId}
              onSelect={handleSelect}
              onCreate={() => setShowCreateModal(true)}
            />
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {!selectedId ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <Trophy size={48} className="mx-auto mb-3 text-gray-200" />
              <h2 className="text-lg font-medium text-gray-600 font-unna mb-1">Select a leaderboard</h2>
              <p className="text-sm text-gray-400 font-rasa">
                Choose a leaderboard from the sidebar or create a new one to start competing.
              </p>
            </div>
          ) : detailLoading ? (
            <div className="bg-white rounded-lg shadow-md p-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : lb ? (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Leaderboard header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 font-unna">{lb.name}</h2>
                  <p className="text-xs text-gray-400 font-rasa mt-0.5">
                    Created by @{lb.creator_username} &middot; {lb.member_count} members &middot; {(lb.metrics || []).length} metrics
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchLeaderboardDetail(selectedId)}
                    className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                    title="Refresh rankings"
                  >
                    <RefreshCw size={16} />
                  </button>
                  {lb.is_owner ? (
                    <>
                      <button
                        onClick={() => setShowEditModal(true)}
                        className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                        title="Edit leaderboard"
                      >
                        <Settings size={16} />
                      </button>
                      <button
                        onClick={handleDelete}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete leaderboard"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleLeave}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Leave leaderboard"
                    >
                      <LogOut size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Rankings table */}
              <div className="p-6">
                <LeaderboardTable rankings={rankings} metrics={lb.metrics || []} />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateLeaderboardModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
        />
      )}

      {showEditModal && lb && (
        <EditLeaderboardModal
          leaderboard={lb}
          onClose={() => setShowEditModal(false)}
          onUpdate={handleUpdate}
        />
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText="Cancel"
        isDestructive={confirmModal.isDestructive}
      />
    </div>
  );
};

export default LeaderboardPage;
